/**
 * Trade stations — phase 8's venue. One seeded station per populated
 * system, orbiting in the gap between the two innermost planets: the
 * dockable hub where the market, escrowed trades, auction board (and
 * phase 9's social space, phase 10's mission boards) live.
 *
 * Pure-seed contract, same as everything else: presence, orbit, pads and
 * name are f(system seed); the pose is f(seed, wallclock) via the shared
 * clock — every client and the server agree on where the station is and
 * exactly how each docked ship sits on its pad.
 */
import { rngFromString, range } from "./rng.js";
import * as pmath from "./pmath.js";
import { rotateAbout, WORLD_SCALE, type SystemDef } from "./universe.js";

export interface StationPad {
  /** Station-local position (m) of the pad's REST point (hull center). */
  local: [number, number, number];
  /** Station-local outward normal (ship up while docked). */
  up: [number, number, number];
}

export interface StationDef {
  /** `${system.seed}:station` — seeds visuals and the name. */
  seed: string;
  name: string;
  /** Orbit radius around the star (m) and Kepler-ish period (s). */
  orbitRadius: number;
  periodS: number;
  phase: number;
  /** Slow roll about the orbit normal (s per revolution). */
  spinPeriodS: number;
  /** Orbit plane normal (unit). */
  normal: [number, number, number];
  /** Overall hull radius (m) — collision/docking envelope. */
  radius: number;
  pads: StationPad[];
}

export interface StationPose {
  center: [number, number, number];
  spinAngle: number;
}

const PAD_COUNT = 4;
/** Docking is offered inside this range of a pad (m). */
export const DOCK_RANGE = 90;
/** Max approach speed for a dock to engage (m/s). */
export const DOCK_SPEED = 40;

const SUFFIX = ["Reach", "Anchorage", "Haven", "Gate", "Terminal", "Spire", "Rest", "Crossing"];

/** The system's station, or null (sparse systems go without). */
export function stationFor(def: SystemDef): StationDef | null {
  if (def.planets.length < 3) return null;
  const rng = rngFromString(`${def.seed}:station`);
  // Park in the gap between the two innermost planet orbits — travelled
  // space, never inside a planet's carry band.
  const orbits = def.planets
    .filter((p) => !p.parentId)
    .map((p) => pmath.hypot(p.position[0], p.position[1], p.position[2]))
    .sort((a, b) => a - b);
  if (orbits.length < 2) return null;
  const orbitRadius = (orbits[0] + orbits[1]) / 2;
  // Same stately Kepler scaling the planets use (distance reference scaled with
  // WORLD_SCALE so the station's angular period matches the planets', not crawls).
  const periodS = 27000 * pmath.pow(orbitRadius / (70000 * WORLD_SCALE), 1.5);
  const radius = range(rng, 130, 190);
  const pads: StationPad[] = [];
  for (let i = 0; i < PAD_COUNT; i++) {
    const a = (i / PAD_COUNT) * Math.PI * 2;
    // Pads sit on the outer ring's flat top, facing spin-up.
    pads.push({
      local: [pmath.cos(a) * radius * 0.82, radius * 0.18, pmath.sin(a) * radius * 0.82],
      up: [0, 1, 0],
    });
  }
  return {
    seed: `${def.seed}:station`,
    name: `${def.name} ${SUFFIX[Math.floor(rng() * SUFFIX.length)]}`,
    orbitRadius,
    periodS,
    phase: rng() * Math.PI * 2,
    spinPeriodS: range(rng, 900, 1600),
    // Fixed +Y: pad/world transforms stay a single rotation — no compose
    // ambiguity between the mesh orientation and the shared math.
    normal: [0, 1, 0],
    radius,
    pads,
  };
}

/** Station pose at time t — same clock contract as systemPoses. */
export function stationPose(st: StationDef, t: number): StationPose {
  const th = st.phase + ((t % st.periodS) / st.periodS) * Math.PI * 2;
  // Base point on the orbit plane: rotate a reference radial about normal.
  const ref: [number, number, number] = [st.orbitRadius, 0, 0];
  const center = rotateAbout(ref, st.normal, th);
  return {
    center,
    spinAngle: ((t % st.spinPeriodS) / st.spinPeriodS) * Math.PI * 2,
  };
}

/**
 * World transform of a pad at time t: position + the station's spin
 * rotation (about its orbit normal — the station rolls in its plane).
 * Returns [pos, up] world vectors.
 */
export function padWorld(
  st: StationDef,
  pose: StationPose,
  pad: number,
): {
  pos: [number, number, number];
  up: [number, number, number];
  /** Docked nose direction (tangent along the ring — every client agrees). */
  nose: [number, number, number];
} {
  const p = st.pads[pad] ?? st.pads[0];
  const rp = rotateAbout(p.local, st.normal, pose.spinAngle);
  const ru = rotateAbout(p.up, st.normal, pose.spinAngle);
  // Local tangent = up × radial direction of the pad.
  const rl = pmath.hypot(p.local[0], 0, p.local[2]) || 1;
  const tx = (p.up[1] * p.local[2] - p.up[2] * 0) / rl;
  const tz = (p.up[0] * 0 - p.up[1] * p.local[0]) / rl;
  const rn = rotateAbout([tx, 0, tz], st.normal, pose.spinAngle);
  return {
    pos: [pose.center[0] + rp[0], pose.center[1] + rp[1], pose.center[2] + rp[2]],
    up: ru,
    nose: rn,
  };
}
